import React, { useEffect, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import useDebounce from '../../hooks/useDebounce';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { getWithAuth, postWithAuth } from '../../api';
import { polygon, point } from '@turf/helpers';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import additionalMessages from '../../messages';
import { Modal, Button, Form, Message, Grid } from 'semantic-ui-react';
import {
  getDropdownOptions,
  getSelectOptions,
  sortObjectsByKey,
} from '../../utils/useful_functions';
import filterWorker from '../../workers/filter.worker';
import { distanceBetweenTwoPoints } from './aplayout/AccessPointProperties';
import { MIN_LINK_LENGTH_M } from 'src/app.constants';
import { SM_TEMPLATE_ID_KEY, PMPLinkTemplate } from '../map/PMPLinkTemplate';
import {
  createAPTableQueryKey,
  createPMPLinkTableQueryKey,
} from '../../components/tables/internal/TableQueryKeys';

async function getSmRegistrationLimit(apId, projectId) {
  const ap = await getWithAuth(`project/${projectId}/access_point/${apId}`);

  return ap.radios.reduce((limit, radio) => {
    const registrationLimit = parseInt(
      radio?.equipment?.sm_registration_limit,
      10
    );
    return Number.isNaN(registrationLimit) ? limit : limit + registrationLimit;
  }, 0);
}

function getSubscribersInRange(accessPoints, subscriberSites, apId) {
  if (!apId) {
    return [];
  }

  const selectedAp = accessPoints.features.find(
    (ap) => ap.properties.id === apId
  );

  if (!selectedAp) {
    return [];
  }

  const { geometry: apGeometry, properties: apProperties } = selectedAp;
  const antennaLat = get(apProperties, 'radios.0.antennas.0.latitude');
  const antennaLng = get(apProperties, 'radios.0.antennas.0.longitude');
  const apLat = antennaLat != null ? antennaLat : apProperties.latitude;
  const apLng = antennaLng != null ? antennaLng : apProperties.longitude;
  const selectedApPolygon = polygon(apGeometry.coordinates);

  return subscriberSites.features.reduce((sites, subscriberSite) => {
    const { properties, geometry } = subscriberSite;

    if (
      booleanPointInPolygon(point(geometry.coordinates), selectedApPolygon) &&
      distanceBetweenTwoPoints(
        {
          latitude: apLat,
          longitude: apLng,
        },
        {
          latitude: geometry.coordinates[1],
          longitude: geometry.coordinates[0],
        }
      ) > MIN_LINK_LENGTH_M
    ) {
      sites.push({ ...properties, ...geometry });
    }

    return sites;
  }, []);
}

function getErrorMessage(error) {
  if (!error) {
    return null;
  }

  return error.message ?? error.detail ?? null;
}

function useAttachSubscribers({ apId, onClose, successCallback }) {
  const { formatMessage } = useIntl();
  const qc = useQueryClient();
  const initializedApIdRef = useRef(null);
  const [spFilter, setSPFilter] = useState('');
  const { projectId, subscriberSites, accessPoints } = useSelector(
    (state) => state.mainFrame
  );

  const apOptions = useMemo(() => {
    const properties = accessPoints.features.map((obj) => obj.properties);
    return sortObjectsByKey(getSelectOptions(properties), 'text');
  }, [accessPoints]);

  const initialApId = apId ?? apOptions[0]?.key ?? '';
  const debounceFilter = useDebounce(spFilter, 500);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      apId: initialApId,
      spIds: [],
    },
    mode: 'onChange',
  });

  const selectedApId = watch('apId');
  const selectedSpCount = watch('spIds')?.length ?? 0;

  const spsInRange = useMemo(
    () => getSubscribersInRange(accessPoints, subscriberSites, selectedApId),
    [accessPoints, subscriberSites, selectedApId]
  );
  const subscriberIdsInRange = useMemo(
    () => spsInRange.map((site) => site.id),
    [spsInRange]
  );

  const onCloseHandler = () => onClose?.();

  const { data: smRegistrationLimit = 0, isFetched: hasSmRegistrationLimit } =
    useQuery({
      queryKey: [
        'project',
        projectId,
        'access_point',
        selectedApId,
        'sm_registration',
      ],
      queryFn: () => getSmRegistrationLimit(selectedApId, projectId),
      enabled: Boolean(projectId) && Boolean(selectedApId),
      retry: false,
    });

  const {
    data: sitesInRange,
    error: sitesInRangeError,
    isFetched: hasSitesInRange,
  } = useQuery({
    queryKey: [
      'project',
      projectId,
      'access_point',
      selectedApId,
      'sites_in_range',
      subscriberIdsInRange,
    ],
    queryFn: () =>
      postWithAuth(
        `project/${projectId}/access_point/${selectedApId}/sites_in_range`,
        subscriberIdsInRange
      ),
    enabled:
      Boolean(projectId) &&
      Boolean(selectedApId),
    retry: false,
  });

  const unconnectedSM = sitesInRange?.available_ids ?? [];
  const allSubscribers = sitesInRange?.attached_count ?? 0;

  useEffect(() => {
    if (!selectedApId) {
      initializedApIdRef.current = null;
      setValue('spIds', [], { shouldValidate: true });
      return;
    }

    if (initializedApIdRef.current === selectedApId) {
      return;
    }

    if (subscriberIdsInRange.length === 0) {
      initializedApIdRef.current = selectedApId;
      setValue('spIds', [], { shouldValidate: true });
      return;
    }

    if (!hasSitesInRange) {
      return;
    }

    initializedApIdRef.current = selectedApId;
    setValue('spIds', unconnectedSM, { shouldValidate: true });
  }, [
    hasSitesInRange,
    selectedApId,
    setValue,
    subscriberIdsInRange.length,
    unconnectedSM,
  ]);

  const { data: filteredItems = [] } = useQuery({
    queryKey: ['filter-subscribers', subscriberIdsInRange, debounceFilter],
    queryFn: ({ signal }) =>
      new Promise((resolve, reject) => {
        const worker = new filterWorker();
        const handleAbort = () => {
          worker.terminate();
          reject(new Error('Filter aborted'));
        };

        if (signal?.aborted) {
          handleAbort();
          return;
        }

        signal?.addEventListener('abort', handleAbort, { once: true });
        worker.onmessage = (message) => {
          signal?.removeEventListener('abort', handleAbort);
          resolve(JSON.parse(message.data));
          worker.terminate();
        };
        worker.postMessage(
          JSON.stringify({
            command: 'filter',
            items: spsInRange,
            filter: debounceFilter,
          })
        );
      }),
    enabled: spsInRange.length > 0,
    retry: false,
  });

  const attachSubscribers = useMutation({
    mutationFn: ({ spIds }) => {
      const templateId = localStorage.getItem(SM_TEMPLATE_ID_KEY);
      return postWithAuth(`project/${projectId}/subscribers`, {
        existing_id: templateId,
        site_ids: spIds,
        ap_id: selectedApId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: createPMPLinkTableQueryKey(projectId) });
      qc.invalidateQueries({ queryKey: createAPTableQueryKey(projectId) });
      if (selectedApId) {
        qc.invalidateQueries({
          queryKey: [
            'project',
            projectId,
            'access_point',
            selectedApId,
            'subscribers',
          ],
        });
      }
      onCloseHandler();
      successCallback?.();
    },
  });

  const apName = apId
    ? (apOptions.find((ap) => ap.key === apId)?.text ?? null)
    : null;

  const spOptions = useMemo(
    () => getDropdownOptions(filteredItems),
    [filteredItems]
  );

  const clearSpSelection = () => {
    setValue('spIds', [], { shouldValidate: true });
  };

  const handleApChange = (value) => {
    initializedApIdRef.current = null;
    setValue('apId', value, { shouldValidate: true });
    clearSpSelection();
    attachSubscribers.reset();
  };

  const errMsg =
    getErrorMessage(attachSubscribers.error) ??
    getErrorMessage(sitesInRangeError) ??
    (spsInRange.length === 0
      ? formatMessage(additionalMessages.noSMsInRange)
      : null);

  const onSubmit = (values) => {
    attachSubscribers.mutate(values);
  };

  return {
    control,
    handleSubmit,
    errors,
    isValid,
    selectedSpCount,
    apOptions,
    spOptions,
    apName,
    smRegistrationLimit,
    hasSmRegistrationLimit,
    allSubscribers,
    spFilter,
    setSPFilter,
    errMsg,
    onCloseHandler,
    onSubmit,
    isSubmitting: attachSubscribers.isPending,
    handleApChange,
  };
}

function SubscriberSitesField({
  control,
  validateSubscriberSites,
  spOptions,
  spFilter,
  setSPFilter,
  errors,
  selectedSpCount,
  smRegistrationLimit,
  hasSmRegistrationLimit,
  allSubscribers,
}) {
  const { formatMessage } = useIntl();

  return (
    <Grid.Column style={{ height: '40vh' }}>
      <Form.Input
        className="flex-grow-0"
        label={formatMessage(additionalMessages.subscriberSites)}
        placeholder={formatMessage(additionalMessages.filter)}
        fluid
        icon={{
          name: 'cancel',
          link: true,
          onClick: () => setSPFilter(''),
          title: formatMessage(additionalMessages.clearFilter),
        }}
        value={spFilter}
        onChange={(event) => setSPFilter(event.target.value)}
        autoFocus
      />
      <Controller
        control={control}
        name="spIds"
        rules={{
          validate: {
            required: (value) =>
              value?.length > 0 ||
              formatMessage(additionalMessages.requiredSelectError, {
                fieldName: 'subscriber sites',
              }),
            subscriberSites: (value) => validateSubscriberSites(value) || true,
          },
        }}
        render={({ field }) => (
          <select
            multiple
            value={field.value}
            onChange={(event) => {
              field.onChange(
                Array.from(event.target.selectedOptions, ({ value }) => value)
              );
            }}
            style={{ width: '100%', height: '100%' }}
          >
            {spOptions}
          </select>
        )}
      />
      <div className="flex-grow-0" style={{ marginTop: '0.5rem' }}>
        {formatMessage(additionalMessages.attachSmCountSummary, {
          existing: allSubscribers,
          selected: selectedSpCount,
          total: allSubscribers + selectedSpCount,
        })}
      </div>
      {hasSmRegistrationLimit &&
      selectedSpCount + allSubscribers > smRegistrationLimit ? (
        <Message className="flex-grow-0" negative>
          <p>
            {formatMessage(
              additionalMessages.apLinksToSMRegistrationLimitError,
              {
                totalNumOfSubscribers: selectedSpCount + allSubscribers,
                numOfSupportedSubscribers: smRegistrationLimit,
              }
            )}
          </p>
        </Message>
      ) : null}
      {errors.spIds && selectedSpCount > 0 ? (
        <Message className="flex-grow-0" negative>
          <p>{errors.spIds.message}</p>
        </Message>
      ) : null}
    </Grid.Column>
  );
}

function AttachSubscribersDialog(props) {
  const { formatMessage } = useIntl();
  const {
    control,
    handleSubmit,
    errors,
    isValid,
    selectedSpCount,
    apOptions,
    spOptions,
    apName,
    smRegistrationLimit,
    hasSmRegistrationLimit,
    allSubscribers,
    spFilter,
    setSPFilter,
    errMsg,
    onCloseHandler,
    onSubmit,
    isSubmitting,
    handleApChange,
  } = useAttachSubscribers({
    apId: props.apId,
    onClose: props.onClose,
    successCallback: props.successCallback,
  });

  return (
    <Modal onClose={onCloseHandler} size="tiny" open>
      <Modal.Header>
        {apName != null ? (
          `Attach Subscribers to ${apName}`
        ) : (
          <FormattedMessage
            id="accessPoints.attachSP"
            defaultMessage="Attach Subscribers"
          />
        )}
        <Button
          circular
          icon="close"
          title={formatMessage(additionalMessages.close)}
          floated="right"
          onClick={onCloseHandler}
        />
      </Modal.Header>
      <Modal.Content>
        <Form className="ui form" onSubmit={handleSubmit(onSubmit)}>
          <Grid stretched>
            <Grid.Row style={{ paddingTop: '0.5rem' }}>
              <Grid.Column>
                <PMPLinkTemplate />
              </Grid.Column>
            </Grid.Row>
            {!props.apId && (
              <Grid.Row>
                <Grid.Column>
                  <Controller
                    control={control}
                    name="apId"
                    rules={{
                      required: formatMessage(
                        additionalMessages.requiredSelectError,
                        { fieldName: 'access point' }
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <Form.Dropdown
                          fluid
                          search
                          selection
                          label={formatMessage(additionalMessages.accessPoints)}
                          options={apOptions}
                          value={field.value}
                          error={fieldState.error != null}
                          onChange={(_, data) => handleApChange(data.value)}
                        />
                        {fieldState.error ? (
                          <Message className="flex-grow-0" negative>
                            <p>{fieldState.error.message}</p>
                          </Message>
                        ) : null}
                      </>
                    )}
                  />
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row>
              <SubscriberSitesField
                control={control}
                validateSubscriberSites={props.validateSubscriberSites}
                spOptions={spOptions}
                spFilter={spFilter}
                setSPFilter={setSPFilter}
                errors={errors}
                selectedSpCount={selectedSpCount}
                smRegistrationLimit={smRegistrationLimit}
                hasSmRegistrationLimit={hasSmRegistrationLimit}
                allSubscribers={allSubscribers}
              />
            </Grid.Row>
          </Grid>
          <div className="modal-actions">
            <Button onClick={onCloseHandler}>
              {formatMessage(additionalMessages.cancel)}
            </Button>
            <Button
              type="submit"
              disabled={!isValid || selectedSpCount === 0}
              loading={isSubmitting}
              color="blue"
            >
              {formatMessage(additionalMessages.ok)}
            </Button>
          </div>
        </Form>
      </Modal.Content>
      {errMsg && (
        <Message negative attached>
          <Message.Header>
            {formatMessage(additionalMessages.attachSMError)}
          </Message.Header>
          <p>{errMsg}</p>
        </Message>
      )}
    </Modal>
  );
}

export default AttachSubscribersDialog;
